import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { chartColors } from 'constants/colors';

import { INSIGHT_TYPES } from '../../../../store/slices/survey/interfaces/IInsightsDashboardResponse';

import type {
  ChartData,
  ChartDataset,
  ChartOptions,
  CoreScaleOptions,
  Scale,
} from 'chart.js';
import type { IInsightsDashboard } from '../../../../store/slices/survey/interfaces/IInsightsDashboardResponse';

import styles from '../scss/MAInsightsDashboardChart.module.scss';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function InsightsDashboardChart({
  dashboardData,
}: { dashboardData: IInsightsDashboard }) {
  const { t } = useTranslation();
  const {
    charcoal, deepAquamarine, azul, lovelyPurple, infoYellow, culturedPearl,
  } = chartColors;

  if (!dashboardData) {
    return null;
  }

  const topicTooltipLabels = (dashboardData[INSIGHT_TYPES.GLOBAL] || []).map((item) => item.title);
  const topicLabels = (dashboardData[INSIGHT_TYPES.GLOBAL]
    || []).map((_, index) => `${t('materialityAssessment.topic')} ${index + 1}`);

  const chartPosition = {
    [INSIGHT_TYPES.SURVEY]: (dashboardData[INSIGHT_TYPES.SURVEY] || []).map((item) => item.averagePosition),
    [INSIGHT_TYPES.INTERVIEW]: (dashboardData[INSIGHT_TYPES.INTERVIEW] || []).map((item) => item.averagePosition),
    [INSIGHT_TYPES.ROUNDTABLE]: (dashboardData[INSIGHT_TYPES.ROUNDTABLE]
      || []).map((item) => item.averagePosition),
    [INSIGHT_TYPES.AI]: (dashboardData[INSIGHT_TYPES.AI] || []).map((item) => item.averagePosition),
  };

  const datasets: ChartDataset<'bar', number[]>[] = [];

  if (dashboardData[INSIGHT_TYPES.SURVEY]?.length) {
    datasets.push({
      label: t('materialityAssessment.surveys'),
      data: chartPosition[INSIGHT_TYPES.SURVEY],
      backgroundColor: deepAquamarine,
      borderRadius: 12,
    });
  }

  if (dashboardData[INSIGHT_TYPES.INTERVIEW]?.length) {
    datasets.push({
      label: t('materialityAssessment.interviews'),
      data: chartPosition[INSIGHT_TYPES.INTERVIEW],
      backgroundColor: lovelyPurple,
      borderRadius: 12,
    });
  }

  if (dashboardData[INSIGHT_TYPES.ROUNDTABLE]?.length) {
    datasets.push({
      label: t('materialityAssessment.roundtables'),
      data: chartPosition[INSIGHT_TYPES.ROUNDTABLE],
      backgroundColor: azul,
      borderRadius: 12,
    });
  }

  if (dashboardData[INSIGHT_TYPES.AI]?.length) {
    datasets.push({
      label: t('materialityAssessment.AIAnalysis'),
      data: chartPosition[INSIGHT_TYPES.AI],
      backgroundColor: infoYellow,
      borderRadius: 12,
    });
  }

  const chartData: ChartData<'bar'> = {
    labels: topicLabels,
    datasets,
  };

  const chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        align: 'start',
        labels: {
          usePointStyle: true,
          boxWidth: 12,
          padding: 20,
          font: {
            size: 10,
            weight: 500,
          },
          color: charcoal,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const idx = tooltipItems[0]?.dataIndex;
            return topicTooltipLabels[idx] || '';
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        title: {
          display: false,
        },
        ticks: {
          font: {
            size: 10,
            weight: 500,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: culturedPearl,
        },
        title: {
          display: false,
        },
        min: 0,
        max: 10,
        ticks: {
          stepSize: 1,
          callback(
            this: Scale<CoreScaleOptions>,
            tickValue: string | number,
          ) {
            return typeof tickValue === 'string' ? Number(tickValue) : tickValue;
          },
        },
      },
    },
    datasets: {
      bar: {
        barPercentage: 0.85,
        categoryPercentage: 0.55,
      },
    },
  };

  return (
    <div className={styles.chartWrapper}>
      <Bar
        data={chartData}
        options={chartOptions}
      />
    </div>
  );
}

export default InsightsDashboardChart;
